/** Helpers bridging ISO datetimes (API) and `<input type="datetime-local">` values. */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** A local calendar day as `YYYY-MM-DD` (no UTC shift) — the `<input type=date>` value. */
export function toIsoDay(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A complete `YYYY-MM-DD` day. Date inputs emit partial years while one is
 *  typed (`0002-…`, `0202-…`), so a leading-zero year doesn't count. */
export function isIsoDay(value: string | null | undefined): value is string {
  return /^[1-9]\d{3}-\d{2}-\d{2}$/.test(value ?? '');
}

/** ISO datetime → local "YYYY-MM-DDTHH:mm" for a datetime-local input. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Local datetime-local value → ISO string (UTC) for the API; '' → null. */
export function fromLocalInput(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** ISO datetime → the user's locale date + time; '—' when absent/invalid. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** `YYYY-MM-DD` → the locale date, read as a *local* calendar day (no UTC shift). */
export function formatIsoDate(day: string | null | undefined): string {
  if (!day) return '—';
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * Inclusive Mon–Fri count between two ISO dates — the leave-day convention
 * (Fri → Mon = 2). Holidays aren't modelled, so the result is a prefill the
 * user can still edit (half-days, org specifics). 0 when the range is invalid.
 */
export function businessDaysBetween(startIso: string, endIso: string): number {
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  let count = 0;
  for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count += 1;
  }
  return count;
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];
// The UI is English-only until i18n is wired (F10); one formatter, built once.
const relativeFormat = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/**
 * "5 minutes ago", "2 days ago", "just now" for a past ISO time; a time a few
 * seconds in the future (a client clock behind the server's) reads "just now".
 * Null when the time is missing or invalid.
 */
export function formatRelativeTime(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const seconds = Math.round((Date.parse(iso) - now) / 1000);
  if (Number.isNaN(seconds)) return null;
  const past = Math.min(seconds, 0);
  for (const [unit, size] of RELATIVE_UNITS) {
    // Round the magnitude: Math.round(-1.5) is -1, which would make 90 min "1 hour ago".
    if (-past >= size) return relativeFormat.format(-Math.round(-past / size), unit);
  }
  return 'just now';
}
